import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Briefcase, Plus, Search, Filter, Clock, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { backendJobService } from '@/services/backendJobService';
import { JobCard } from '@/components/ui/JobCard';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SkipLink } from '@/components/ui/SkipLink';
import { SkeletonJobCard } from '@/components/ui/SkeletonLoaders';
import { EnhancedEmptyState } from '@/components/ui/EnhancedEmptyState';
import type { Job } from '@/types';

export function CustomerJobsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'quotes' | 'active' | 'completed'>(
    (searchParams.get('tab') as any) || 'all'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'quotes'>('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedUrgency, setSelectedUrgency] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 9;

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCustomerJobs();
  }, []);

  useEffect(() => {
    // Update URL when tab changes
    setSearchParams({ tab: activeTab });
  }, [activeTab]);

  const fetchCustomerJobs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const customerJobsData = await backendJobService.getMyJobs();
      setJobs(customerJobsData.jobs);
    } catch (err) {
      console.error('Failed to fetch customer jobs:', err);
      setError('Failed to load your jobs. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getFilteredJobs = () => {
    let filtered = [...jobs];

    // Filter by tab
    if (activeTab === 'quotes') {
      filtered = filtered.filter(job => job.quoteCount > 0 && job.status === 'OPEN');
    } else if (activeTab === 'active') {
      filtered = filtered.filter(job => job.status === 'OPEN' || job.status === 'IN_PROGRESS');
    } else if (activeTab === 'completed') {
      filtered = filtered.filter(job => job.status === 'COMPLETED');
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(job =>
        job.title.toLowerCase().includes(query) ||
        job.description.toLowerCase().includes(query) ||
        job.category.toLowerCase().includes(query) ||
        job.location.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(job => job.category === selectedCategory);
    }

    // Urgency filter
    if (selectedUrgency) {
      filtered = filtered.filter(job => job.urgency === selectedUrgency);
    }

    // Sort
    if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.id).getTime() - new Date(a.id).getTime());
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.id).getTime() - new Date(b.id).getTime());
    } else if (sortBy === 'quotes') {
      filtered.sort((a, b) => (b.quoteCount || 0) - (a.quoteCount || 0));
    }

    return filtered;
  };

  const filteredJobs = getFilteredJobs();
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const paginatedJobs = filteredJobs.slice(startIndex, startIndex + jobsPerPage);

  // Calculate stats
  const allJobsCount = jobs.length;
  const quotesCount = jobs.filter(j => j.quoteCount > 0 && j.status === 'OPEN').length;
  const activeCount = jobs.filter(j => j.status === 'OPEN' || j.status === 'IN_PROGRESS').length;
  const completedCount = jobs.filter(j => j.status === 'COMPLETED').length;
  const totalQuotes = jobs.reduce((sum, job) => sum + (job.quoteCount || 0), 0);

  // Get unique categories
  const categories = Array.from(new Set(jobs.map(j => j.category))).filter(Boolean);

  // Active filters count
  const activeFiltersCount = [searchQuery, selectedCategory, selectedUrgency].filter(Boolean).length;

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedUrgency('');
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20 md:pb-8">
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Breadcrumb */}
        <Breadcrumb items={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'My Jobs' }
        ]} />

        {/* Header */}
        <div className="mb-4 sm:mb-6 mt-3 sm:mt-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Jobs</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">Track your jobs and manage quotes</p>
            </div>
            <Button
              onClick={() => navigate('/create-job')}
              variant="primary"
              leftIcon={<Plus className="w-5 h-5" />}
              aria-label="Post a new job"
            >
              Post New Job
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <Card padding="sm" className="border-l-4 border-l-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-gray-900">{allJobsCount}</div>
                  <div className="text-xs text-gray-600">Total Jobs</div>
                </div>
                <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 opacity-20" aria-hidden="true" />
              </div>
            </Card>
            <Card padding="sm" className="border-l-4 border-l-warning-500">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-gray-900">{quotesCount}</div>
                  <div className="text-xs text-gray-600">Awaiting Review</div>
                </div>
                <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-warning-500 opacity-20" aria-hidden="true" />
              </div>
            </Card>
            <Card padding="sm" className="border-l-4 border-l-primary-500">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-gray-900">{activeCount}</div>
                  <div className="text-xs text-gray-600">Active</div>
                </div>
                <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-primary-500 opacity-20" aria-hidden="true" />
              </div>
            </Card>
            <Card padding="sm" className="border-l-4 border-l-emerald-500">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-gray-900">{completedCount}</div>
                  <div className="text-xs text-gray-600">Completed</div>
                </div>
                <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-500 opacity-20" aria-hidden="true" />
              </div>
            </Card>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex bg-white p-1 rounded-lg border border-gray-200 inline-flex">
            {[
              { id: 'all', label: 'All Jobs', count: allJobsCount },
              { id: 'quotes', label: 'Quotes', count: quotesCount, badge: quotesCount > 0 },
              { id: 'active', label: 'Active', count: activeCount },
              { id: 'completed', label: 'Completed', count: completedCount }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id as any); setCurrentPage(1); }}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all relative ${activeTab === tab.id
                    ? 'bg-primary-50 text-primary-700 font-semibold'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <Badge 
                    variant={tab.badge ? 'warning' : 'neutral'} 
                    size="sm" 
                    className="ml-2"
                  >
                    {tab.count}
                  </Badge>
                )}
              </button>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            icon={<Filter className="w-4 h-4" />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
            {activeFiltersCount > 0 && (
              <Badge variant="secondary" size="sm" className="ml-2">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </div>

        {/* Tab Description */}
        {activeTab === 'quotes' && quotesCount > 0 && (
          <Card className="mb-6 bg-primary-50 border-primary-200">
            <div className="p-4 flex items-start gap-3">
              <FileText className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-primary-900 mb-1">Review Your Quotes</h3>
                <p className="text-sm text-primary-700">
                  You have {quotesCount} job{quotesCount > 1 ? 's' : ''} with quotes from providers. Click on any job to view and compare quotes, then accept the one that works best for you.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Filters</h3>
                {activeFiltersCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Clear All
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search jobs..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm min-h-touch"
                    aria-label="Filter by category"
                  >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Urgency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Urgency
                  </label>
                  <select
                    value={selectedUrgency}
                    onChange={(e) => setSelectedUrgency(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm min-h-touch"
                    aria-label="Filter by urgency"
                  >
                    <option value="">All Urgencies</option>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </div>
              </div>

              {/* Sort */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sort By
                </label>
                <div className="flex gap-2">
                  {[
                    { id: 'newest', label: 'Newest First' },
                    { id: 'oldest', label: 'Oldest First' },
                    { id: 'quotes', label: 'Most Quotes' }
                  ].map((sort) => (
                    <button
                      key={sort.id}
                      onClick={() => setSortBy(sort.id as any)}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors min-h-touch ${sortBy === sort.id
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      aria-pressed={sortBy === sort.id}
                    >
                      {sort.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Results Summary */}
        {!isLoading && (
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold">{filteredJobs.length}</span> job{filteredJobs.length !== 1 ? 's' : ''}
              {activeFiltersCount > 0 && ' (filtered)'}
            </p>
            {totalQuotes > 0 && (
              <p className="text-sm text-gray-600">
                <span className="font-semibold">{totalQuotes}</span> total quote{totalQuotes !== 1 ? 's' : ''} received
              </p>
            )}
          </div>
        )}

        {/* Jobs Grid */}
        <div className="min-h-[400px]">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <SkeletonJobCard key={i} />
              ))}
            </div>
          ) : error ? (
            <Card padding="xl" className="text-center border-error-200 bg-error-50">
              <AlertCircle className="w-12 h-12 text-error-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-error-900 mb-2">Error Loading Jobs</h3>
              <p className="text-error-700 mb-4">{error}</p>
              <Button onClick={fetchCustomerJobs} variant="outline">Try Again</Button>
            </Card>
          ) : filteredJobs.length === 0 ? (
            <EnhancedEmptyState
              icon={Briefcase}
              title={activeFiltersCount > 0 ? 'No jobs match your filters' : `No ${activeTab} jobs`}
              description={
                activeFiltersCount > 0 
                  ? 'Try adjusting your filters to see more results'
                  : activeTab === 'all'
                  ? 'Get started by posting your first job'
                  : `You don't have any ${activeTab} jobs yet`
              }
              action={
                activeFiltersCount > 0 
                  ? { label: 'Clear Filters', onClick: clearFilters }
                  : { label: 'Post a Job', onClick: () => navigate('/create-job'), icon: <Plus className="w-4 h-4" /> }
              }
              size="md"
              illustration="pattern"
            />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedJobs.map((job) => (
                  <div key={job.id} className="relative">
                    {activeTab === 'quotes' && job.quoteCount > 0 ? (
                      // Enhanced card for quotes tab
                      <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full" onClick={() => navigate(`/jobs/${job.id}`)}>
                        <div className="p-6">
                          {/* Job Title & Status */}
                          <div className="flex items-start justify-between mb-3">
                            <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 flex-1">
                              {job.title}
                            </h3>
                            <Badge variant="warning" size="sm" className="ml-2 flex-shrink-0">
                              {job.quoteCount} Quote{job.quoteCount > 1 ? 's' : ''}
                            </Badge>
                          </div>

                          {/* Job Description */}
                          <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                            {job.description}
                          </p>

                          {/* Job Details */}
                          <div className="flex flex-wrap gap-2 mb-4">
                            <Badge variant="secondary" size="sm">{job.category}</Badge>
                            {job.urgency && (
                              <Badge 
                                variant={job.urgency === 'HIGH' ? 'error' : job.urgency === 'MEDIUM' ? 'warning' : 'neutral'} 
                                size="sm"
                              >
                                {job.urgency}
                              </Badge>
                            )}
                          </div>

                          {/* Quote Summary Section */}
                          <div className="pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                                Quotes Received
                              </span>
                              <FileText className="w-4 h-4 text-primary-600" />
                            </div>
                            
                            <div className="bg-primary-50 rounded-lg p-3 mb-3">
                              <div className="text-sm text-primary-900 font-medium mb-1">
                                {job.quoteCount} provider{job.quoteCount > 1 ? 's' : ''} interested
                              </div>
                              <div className="text-xs text-primary-700">
                                Click to view and compare all quotes
                              </div>
                            </div>

                            <Button 
                              size="sm" 
                              fullWidth
                              onClick={() => navigate(`/jobs/${job.id}`)}
                            >
                              Compare Quotes
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ) : (
                      // Standard job card for other tabs
                      <>
                        <JobCard
                          job={job}
                          onView={(id) => navigate(`/jobs/${id}`)}
                          showQuoteCount
                        />
                        {job.quoteCount > 0 && job.status === 'OPEN' && (
                          <div className="absolute top-4 right-4">
                            <Badge variant="warning" size="sm" className="shadow-md">
                              {job.quoteCount} Quote{job.quoteCount > 1 ? 's' : ''}
                            </Badge>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center mt-8 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  
                  <div className="flex gap-1">
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      // Show first, last, current, and adjacent pages
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-colors min-h-touch min-w-touch ${currentPage === page
                                ? 'bg-primary-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                              }`}
                            aria-label={`Go to page ${page}`}
                            aria-current={currentPage === page ? 'page' : undefined}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={i} className="px-2 text-gray-400">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
